<template>
  <v-window :value="step">
    <v-window-item :value="steps.FORM">
      <v-form :ref="CLIENT_FORM_NAME">
        <edit-client-form
          @closeModal="$emit('closeModal')"
          :fields-api-validation.sync="fieldsApiValidation"
          :pesel-required="isDealWon"
          @update:agreement="handleAgreementsUpdate($event)"
          v-model="client">
          <template v-slot:customErrorAction="{clientId}">
            <slot name="customErrorAction" :clientId="clientId"></slot>
          </template>
        </edit-client-form>
      </v-form>
      <v-layout class="px-2 mt-4">
        <fp-radio-group
            xs12
            row
            :label="I18NGetter().useCreateClientWindowsForm.VERIFY_WITH"
            v-model="client.verificationType"
            :items="verificationTypesDictionary" />
      </v-layout>
      <alert-message v-if="areScansEnabled && !isDealWon" class="mt-2">
        {{ $i18nFormatter(I18NGetter().useCreateClientWindowsForm.SCANS_ACCESS_INFO, {endDate: scanAccessEndDateFormatted}) }}
      </alert-message>
    </v-window-item>
    <v-window-item :value="steps.VERIFICATION">
      <v-row pt-6>
        <v-col cols="12">
          <v-form :ref="CLIENT_VERIFICATION_FORM_NAME">
            <v-layout v-if="isSMS" wrap class="pa-4">
              <v-flex xs12>
                <p class="body-1 font-weight-medium">{{ I18NGetter().useCreateClientWindowsForm.ENTER_SMS_CODE }} {{client.phone}}:</p>
              </v-flex>
              <v-flex xs12 md7 sm8>
                <sms-input v-model="client.smsCode"/>
              </v-flex>
            </v-layout>
            <add-client-scans
              v-else
              :client-documents="client.clientDocuments"/>
          </v-form>
        </v-col>
      </v-row>
    </v-window-item>
  </v-window>
</template>

<script lang="ts">
import {Vue, Component, Prop} from 'vue-property-decorator';
import {IMultiformRadioButtonDictionary} from '@/modules/multiForm/shared/IMultiformDictionary';
import {Agreements, AgreementType, ClientAgreements} from '@/modules/client/views/client-agreements/Agreements';
import {CreateClientFormStep} from '@/modules/client/enums/CreateClientFormStep';
import Client from '@/models/Client';
import EditClientForm from '@/modules/deals/views/edit-deal/EditClientForm.vue';
import AddClientScans from '@/modules/client/views/create-client/AddClientScans.vue';
import {ICreateClientWindowsValidation} from '@/modules/client/models/CreateClientWindowsValidation';
import SmsInput from '@/components/commons/SmsInput.vue';
import {I18NGetter} from '@/services/enumTranslator/I18NGetter';
import {I18NInterface} from '@/services/enumTranslator/I18NInterface';
import AlertMessage from '@/components/commons/message/AlertMessage.vue';
import DateUtils from '@/services/utils/DateUtils';
import { DealStage } from '@/modules/deals/enums/DealStage';

@Component({
  components: {SmsInput, AddClientScans, EditClientForm, AlertMessage,},
})
export default class CreateClientWindowsForm extends Vue implements ICreateClientWindowsValidation {
  @Prop({required: true, })
  public readonly client!: Client;

  @Prop({required: true, default: CreateClientFormStep.FORM, type: Number,})
  public readonly step!: CreateClientFormStep;

  @Prop({required: false, default: '',})
  public readonly scanAccessEndDate?: string;

  @Prop({required: false, default: null,})
  public readonly dealStage?: DealStage | null;

  public readonly steps: typeof CreateClientFormStep = CreateClientFormStep;
  public I18NGetter: () => I18NInterface = I18NGetter;
  public readonly CLIENT_FORM_NAME: string = 'CLIENT_FORM';
  public readonly CLIENT_VERIFICATION_FORM_NAME: string = 'CLIENT_VERIFICATION_FORM_NAME';

  public readonly verificationTypesDictionary: IMultiformRadioButtonDictionary<AgreementType> = [
    {
      name_pl: I18NGetter().useCreateClientWindowsForm.SMS,
      disabled: this.isDealWon,
      type: AgreementType.SMS,
    },
    {
      name_pl: I18NGetter().useCreateClientWindowsForm.SCANS,
      type: AgreementType.SCAN,
      disabled: !this.areScansEnabled,
      tooltip: this.isDealWon ? '' : I18NGetter().useCreateClientWindowsForm.SCANS_ACCESS_TOOLTIP_SHORT,
    },
  ];

  public fieldsApiValidation: Record<string, string | null | boolean> = {
    phone: null,
    pesel: null,
    email: null,
    nip: null,
    isIndividual: !this.client.isCompany,
  };

  public get isDealPending(): boolean {
    return this.dealStage === DealStage.PENDING;
  };

  public get isDealWon(): boolean {
    return this.dealStage === DealStage.WON;
  };

  public get isSMS(): boolean {
    return this.client.verificationType === AgreementType.SMS;
  };

  get isCompany(): boolean {
    return this.client.isCompany;
  }

  get scanAccessEndDateFormatted(): string {
    return this.scanAccessEndDate ? DateUtils.getFormatedDate(this.scanAccessEndDate, 'DD.MM.YYYY HH:mm') : '';
  }

  get areScansEnabled(): boolean {
    return (this.isDealPending || this.isDealWon) &&
      !!this.scanAccessEndDate && !DateUtils.isPastDate(this.scanAccessEndDate);
  }

  validateClientForm(): boolean {
    const isAnyApiErrors = Object.values(this.fieldsApiValidation).every((v) => typeof v === 'string');
    return !isAnyApiErrors && this.$validate(this.CLIENT_FORM_NAME, undefined, true);
  }

  validateClientVerificationForm(): boolean {
    return this.$validate(this.CLIENT_VERIFICATION_FORM_NAME);
  }

  handleAgreementsUpdate(newValues: {keysChanged: (keyof ClientAgreements)[], agreements: Agreements}) {
    this.client.agreements = newValues.agreements;
  }
}
</script>
<style scoped lang="scss">
  .sms-input{
    ::v-deep input {
      font-weight: 600;
      font-size: 24px;
    }
  }
  :deep(.v-radio) {
    .v-label {
      pointer-events: none;

      .v-icon {
        pointer-events: all;
      }
    }
  }
</style>
